<template>
  <el-form-item-extended v-if="wrapFormItem && !validationForm" :label="$t('flight-orders.columns.orderData.fullName') + ' *'">
    <edit-order-field-input :flight-order="flightOrder"
                            :field="field"
                            :placeholder="placeholder"
                            :disp-value="dispValue"
                            :input-icon="inputIcon"
                            :link="link"
                            :list-object="listObject"
                            :disabled="disabled"
                            :title="placeholder"
                            :exceptions="exceptions"
                            @change="onChange"
                            @open-rules="$emit('open-rules', $event)"
    />
  </el-form-item-extended>
  <el-form-item-extended v-else-if="validationForm"
                         :validator="cValidator"
                         :messages="cValidationMessage">
    <edit-order-field-input :flight-order="flightOrder"
                            :field="field"
                            :placeholder="placeholder"
                            :disp-value="dispValue"
                            :input-icon="inputIcon"
                            :link="link"
                            :list-object="listObject"
                            :disabled="disabled"
                            :title="placeholder"
                            :exceptions="exceptions"
                            @change="onChange"
                            @input="onInput"
                            @open-rules="$emit('open-rules', $event)"
    />
  </el-form-item-extended>
  <edit-order-field-input v-else
                          :flight-order="flightOrder"
                          :field="field"
                          :placeholder="placeholder"
                          :disp-value="dispValue"
                          :input-icon="inputIcon"
                          :link="link"
                          :list-object="listObject"
                          :disabled="disabled"
                          :title="placeholder"
                          :exceptions="exceptions"
                          @change="onChange"
                          @open-rules="$emit('open-rules', $event)"
  />
</template>

<script>
// npm
import moment from 'moment'
import { split, join, trim } from 'lodash'
// models
// rest
// utils
import { getPortById } from '@/utils/ports'
import { getRoleUserById } from '@/utils/user'
// components
import PortSelect from '@/components/common/PortSelect'
import UserSelect from '@/components/common/UserSelect'
import EditOrderFieldInput from '@/views/flight-order/components/EditOrderFieldInput'

function getISOFormattedDate (dateValue) {
  return moment.utc(
    moment(dateValue).format('DD.MM.YYYY'),
    'DD.MM.YYYY'
  ).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
}

const fields = {
  'flightDateString': {
    type: 'date',
    innerModel: false,
    modelField: 'flightDate',
    placeholderKey: 'flight-orders.depDate',
    callback (value, model, modelField) {
      model[modelField] = getISOFormattedDate(value)
    }
  },
  'depPortLabels.code': {
    type: 'port',
    innerModel: false,
    modelField: 'depPortId',
    callback (value, model, modelField) {
      const port = getPortById(value)
      model.orderData.from = port.labels.code + ' (' + port.labels.label + ')'
    }
  },
  'arrPortLabels.code': {
    type: 'port',
    innerModel: false,
    modelField: 'arrPortId',
    callback (value, model, modelField) {
      const port = getPortById(value)
      model.orderData.to = port.labels.code + ' (' + port.labels.label + ')'
    }
  },
  'shortName': {
    type: 'string',
    innerModel: true,
    modelField: 'fullName',
    callback (value, model, modelField) {
      const nameParts = split(trim(value), /[\t\n ]+/)
      model[modelField] = join(nameParts, ' ')
      model['lastName'] = nameParts[0]
      model['firstName'] = nameParts[1]
      model['middleName'] = nameParts[2]
    }
  },
  'orderData.docNum': {
    type: 'string',
    innerModel: true,
    modelField: 'docNum'
  },
  'orderData.mvz': {
    type: 'string',
    innerModel: true,
    modelField: 'mvz'
  },
  'orderData.mvzsppType': {
    type: 'string',
    innerModel: true,
    modelField: 'mvzsppType'
  },
  'organizationId': {
    type: 'string',
    innerModel: true,
    modelField: 'organizationId',
    placeholderKey: 'flight-orders.paxList.organization'
  },
  'birthDateStr': {
    type: 'date',
    innerModel: true,
    modelField: 'birthDate',
    callback (value, model, modelField) {
      model[modelField] = getISOFormattedDate(value)
    }
  },
  'orderData.comments': {
    type: 'textarea',
    innerModel: true,
    modelField: 'comments'
  },
  'orderData.contacts': {
    type: 'string',
    innerModel: true,
    modelField: 'contacts'
  },
  'orderData.email': {
    type: 'string',
    innerModel: true,
    modelField: 'email'
  },
  'orderData.initiator': {
    type: 'string',
    innerModel: false,
    modelField: 'initiatorUserId',
    placeholderKey: 'flight-orders.paxList.initiator',
    role: 'INITIATOR',
    callback (value, model, modelField) {
      model[modelField] = value
      const user = getRoleUserById('INITIATOR', value)
      console.warn('get user by id: %o %o', value, user)
      model.orderData['initiator'] = user.name
      model.orderData['initiatorEmail'] = user.email
      model.orderData['initiatorPhone'] = user.phone
    }
  },
  'orderData.initiatorEmail': {
    innerModel: true,
    modelField: 'initiatorEmail'
  },
  'orderData.initiatorPhone': {
    innerModel: true,
    modelField: 'initiatorPhone'
  },
  'orderData.timeFrom': {
    type: 'time',
    innerModel: true,
    modelField: 'timeFrom'
  },
  // cargo-specified fields
  'orderData.placeCount': {
    type: 'string',
    innerModel: true,
    modelField: 'placeCount'
  },
  'orderData.name': {
    type: 'string',
    innerModel: true,
    modelField: 'name',
    placeholderKey: 'flight-orders.paxList.cargoName'
  },
  'orderData.dimensions': {
    type: 'string',
    innerModel: true,
    modelField: 'dimensions'
  },
  'orderData.weight': {
    type: 'string',
    innerModel: true,
    modelField: 'weight'
  },
  'orderData.packType': {
    type: 'string',
    innerModel: true,
    modelField: 'packType'
  },
  'orderData.transportType': {
    type: 'string',
    innerModel: true,
    modelField: 'transportType'
  },
  'orderData.rulesAccepted': {
    type: 'checkbox',
    innerModel: true,
    modelField: 'rulesAccepted'
  }
}

export default {
  name: 'EditOrderField',
  components: {PortSelect, UserSelect, EditOrderFieldInput},
  props: {
    dispKey: String, // eslint-disable-line
    dispValue: {
      type: String,
      default: ''
    },
    flightOrder: Object, // eslint-disable-line
    wrapFormItem: {
      type: Boolean,
      default: true
    },
    validationForm: {
      type: Boolean,
      default: false
    },
    v: {
      type: Object,
      default () {
        return null
      }
    },
    inputIcon: {
      type: String,
      default: ''
    },
    link: {
      type: String,
      default: null
    },
    listObject: {
      type: Object,
      default () {
        return null
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    exceptions: {
      type: Array,
      default () {
        return []
      }
    }
  },
  data () {
    return {
      validationMessages: {
        // flightOrder: {
        flightDate: {
          required: this.$t('administration.users.requiredField'),
          invalidDate: this.$t('administration.users.invalidDate')
        },
        depPortId: {
          required: this.$t('administration.users.requiredField')
        },
        arrPortId: {
          required: this.$t('administration.users.requiredField')
        },
        initiatorUserId: {
          required: this.$t('administration.users.requiredField')
        },
        orderData: {
          birthDate: {
            required: this.$t('administration.users.requiredField'),
            invalidDate: this.$t('administration.users.invalidDate')
          },
          email: {
            maxLength: this.$t('administration.users.maxLength'),
            invalidEmail: this.$t('administration.users.invalidEmail')
          },
          docNum: {
            required: this.$t('administration.users.requiredField')
          },
          mvzsppType: {
            required: this.$t('administration.users.requiredField')
          },
          mvz: {
            required: this.$t('administration.users.requiredField')
          },
          fullName: {
            required: this.$t('administration.users.requiredField')
          },
          name: {
            required: this.$t('administration.users.requiredField')
          },
          transportType: {
            required: this.$t('administration.users.requiredField')
          },
          weight: {
            required: this.$t('administration.users.requiredField')
          },
          dimensions: {
            required: this.$t('administration.users.requiredField')
          },
          packType: {
            required: this.$t('administration.users.requiredField')
          },
          placeCount: {
            required: this.$t('administration.users.requiredField')
          },
          organizationId: {
            required: this.$t('administration.users.requiredField')
          },
          rulesAccepted: {
            required: this.$t('flight-orders.rules-error'),
            value: this.$t('flight-orders.rules-error')
          }
        }
        // }
      }
    }
  },
  computed: {
    field () {
      return fields[this.dispKey]
    },
    placeholder () {
      if (!this.field) {
        return ''
      }
      // TODO check whether is equal to field-name
      return this.$t(this.field.placeholderKey || ('flight-orders.paxList.' + this.field.modelField))
    },
    cValidator () {
      if (this.field.innerModel) {
        return this.v.flightOrder.orderData[this.field.modelField]
      } else {
        return this.v.flightOrder[this.field.modelField]
      }
    },
    cValidationMessage () {
      if (this.field.innerModel) {
        return this.validationMessages.orderData[this.field.modelField]
      } else {
        return this.validationMessages[this.field.modelField]
      }
    }
  },
  methods: {
    onChange (value, options) {
      this.debug && console.log('this.$v: %o', this.v)
      this.debug && console.log('this.dispKey: %o', this.dispKey)
      this.$emit('change', value)
      if (this.validationForm) {
        this.cValidator.$touch()
      }
      if (this.dispKey === 'orderData.mvzsppType') {
        this.$emit('change-mvz', value)
      }
      if (this.dispKey === 'orderData.initiator') {
        this.$emit('change-initiator', value)
      }
      if (this.dispKey === 'organizationId') {
        this.$emit('change-organization', value)
      }
      if (this.dispKey === 'birthDateStr') {
        this.$emit('change-birth', value)
      }
      if (this.dispKey === 'flightDateString') {
        this.$emit('change-date', value)
      }
      if (this.dispKey === 'depPortLabels.code') {
        this.$emit('dep-port', value, options)
      }
      if (this.dispKey === 'arrPortLabels.code') {
        this.$emit('arr-port', value, options)
      }
    },
    onInput (value) {
      if (this.cValidator) {
        this.cValidator.$touch()
      }
    },
    openRules (value) {
      this.debug && console.log('value: %o', value)
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/views/flight-order/components/EditOrderField.vue